import type { AddOn as AddOnData } from "schemas/generated/addOns";
import type {
  BillingInterval,
  BillingTier,
  SubscriptionDetails,
} from "schemas/generated/billing";

import { round } from "replo-utils/lib/math";
import { AddOn } from "schemas/addOns";
import { getBillingPlanInfoByTier } from "schemas/billing";

type EstimatedPlanPriceParams = {
  billingTier: BillingTier;
  billingInterval: BillingInterval;
  addOns: AddOnData[];
};

export const getEstimatedPlanPricePerMonth = (
  params: EstimatedPlanPriceParams,
) => {
  const isYearly = params.billingInterval === "yearly";

  const planInfo = getBillingPlanInfoByTier(params.billingTier);

  const totalAddOnPricePerMonth = params.addOns.reduce((total, addOn) => {
    return total + AddOn.fromData(addOn).pricePerMonth();
  }, 0);

  const monthlyPriceForAllProducts =
    planInfo.monthlyPrice + totalAddOnPricePerMonth;

  const yearlyPricePerMonthForAllProducts =
    planInfo.yearlyPricePerMonth + totalAddOnPricePerMonth;

  const priceToDisplay = isYearly
    ? yearlyPricePerMonthForAllProducts
    : monthlyPriceForAllProducts;

  return priceToDisplay;
};

type PercentSavedParams = {
  billingTier: BillingTier;
  addOns: AddOnData[];
};

export const getPercentSavedBySwitchingToYearly = (
  params: PercentSavedParams,
) => {
  const monthlyPricePerMonth = getEstimatedPlanPricePerMonth({
    billingTier: params.billingTier,
    billingInterval: "monthly",
    addOns: params.addOns,
  });
  const yearlyPricePerMonth = getEstimatedPlanPricePerMonth({
    billingTier: params.billingTier,
    billingInterval: "yearly",
    addOns: params.addOns,
  });

  return round((1 - yearlyPricePerMonth / monthlyPricePerMonth) * 100, 0);
};

export const getActiveSubscriptionPricePerMonth = ({
  activeSubscription,
}: {
  activeSubscription: SubscriptionDetails;
}) => {
  const activeProductUnits = activeSubscription.productUnits.filter((unit) => {
    return (
      unit.startedAt !== null &&
      (unit.endedAt === null || unit.endedAt > new Date())
    );
  });

  const totalPricePerMonth = activeProductUnits.reduce((acc, unit) => {
    return acc + unit.monthlyAmount;
  }, 0);

  return totalPricePerMonth;
};

/**
 * Calculates a prorated charge based on the time remaining in the current billing period.
 */
export const getProratedCharge = ({
  fullPeriodPrice,
  billingInterval,
  nextBillingDate,
}: {
  fullPeriodPrice: number;
  billingInterval: BillingInterval;
  nextBillingDate: Date;
}) => {
  const now = new Date();
  const millisecondsRemaining = nextBillingDate.getTime() - now.getTime();

  // Calculate the period start by going back one interval from the next billing date
  const periodStart = new Date(nextBillingDate);
  if (billingInterval === "yearly") {
    periodStart.setFullYear(periodStart.getFullYear() - 1);
  } else {
    periodStart.setMonth(periodStart.getMonth() - 1);
  }

  const totalMillisecondsInPeriod =
    nextBillingDate.getTime() - periodStart.getTime();

  const proratedCharge =
    (fullPeriodPrice * millisecondsRemaining) / totalMillisecondsInPeriod;

  return proratedCharge;
};
